import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

export const SCENE_RENDER_TIMEOUT_MS = 15000;

const ASSET_LOAD_TIMEOUT_MS = 10000;

/**
 * Wait until a scene has finished (trying to) load its assets, so a preview
 * render doesn't capture a half-loaded frame. Resolves immediately if the scene
 * is already done, and always resolves after a safety timeout.
 */
export async function waitForSceneAssets(
    luminary: Luminary,
    scene: Scene,
    timeoutMs = ASSET_LOAD_TIMEOUT_MS,
): Promise<void> {
    if (scene.isDoneTryingToLoad) {
        return;
    }
    await new Promise<void>((resolve) => {
        let resolved = false;
        const finish = () => {
            if (resolved) {
                return;
            }
            resolved = true;
            luminary.off('viewChanged', handleViewChange);
            resolve();
        };
        const handleViewChange = ({ sceneId }: { sceneId: string }) => {
            if (resolved || sceneId !== scene.id) {
                return;
            }
            const currentScene = luminary.getSceneById(sceneId);
            if (currentScene?.isDoneTryingToLoad) {
                finish();
            }
        };
        luminary.on('viewChanged', handleViewChange);
        handleViewChange({ sceneId: scene.id });
        setTimeout(finish, timeoutMs);
    });
}

/**
 * Render a single scene's preview to a PNG data URL. Waits for the scene's
 * assets to load first, then draws to an offscreen canvas sized to the
 * luminary's current resolution/aspect ratio.
 *
 * The shared render queue is locked while the editor draws the live preview to
 * its own canvas, in which case an enqueued render never starts and would
 * otherwise hang forever. An AbortController fires after `timeoutMs` so we fail
 * with a usable result instead of leaving the caller (and the turn) stuck.
 *
 * Returns the data URL, or `null` on timeout / render failure.
 */
export async function renderSceneToDataUrl(
    luminary: Luminary,
    scene: Scene,
    {
        subsceneIndex = 0,
        timeoutMs = SCENE_RENDER_TIMEOUT_MS,
    }: { subsceneIndex?: number; timeoutMs?: number } = {},
): Promise<string | null> {
    await waitForSceneAssets(luminary, scene);

    const canvas = document.createElement('canvas');
    const { width, height } = VideoData.getResolution(
        luminary.resolutionValue,
        luminary.aspectRatio,
    );
    canvas.width = width;
    canvas.height = height;

    const abortController = new AbortController();
    const timeout = setTimeout(() => abortController.abort(), timeoutMs);
    try {
        await scene.renderPreviewToCanvas(canvas, {
            subsceneIndex,
            abortSignal: abortController.signal,
        });
    } catch {
        canvas.remove();
        return null;
    } finally {
        clearTimeout(timeout);
    }
    const imageData = canvas.toDataURL('image/png');
    canvas.remove();
    return imageData;
}
